import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import {
  EnvironmentLogType,
  type EnvironmentLogSummary,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { Code, ConnectError, type Client } from "@connectrpc/connect";
import { createConnectClient } from "server/connect";

type EnvironmentLogClient = Pick<
  Client<typeof EnvironmentService>,
  "getEnvironmentBuildLogStream" | "listEnvironmentLogs" | "getEnvironmentLog"
>;

type EnvironmentLogHistoryClient = Pick<
  Client<typeof EnvironmentService>,
  "listEnvironmentLogs" | "getEnvironmentLog"
>;

export interface EnvironmentLogHistoryPage {
  items: EnvironmentLogSummary[];
  nextPageToken: string;
}

export interface HistoricalEnvironmentLogs {
  content: string;
  etag: string;
  sizeBytes: bigint;
}

export interface PersistedEnvironmentLogs {
  content: Uint8Array;
  etag: string;
  sizeBytes: bigint;
  environmentLogId: string;
}

export type EnvironmentLogSource = "live" | "persisted";

export const ENVIRONMENT_LOG_TYPES = {
  lifecycleScript: EnvironmentLogType.LIFECYCLE_SCRIPT,
} as const;

const ENVIRONMENT_BUILD_LOG_TYPES: ReadonlySet<EnvironmentLogType> = new Set([
  EnvironmentLogType.VERB_BUILD,
  EnvironmentLogType.VM_BUILD,
  EnvironmentLogType.CONTAINER_BUILD,
  EnvironmentLogType.DOCKER_COMPOSE_BUILD,
]);

export interface PollEnvironmentLogsOptions {
  environmentId: string;
  signal: AbortSignal;
  onLogs: (content: string, source: EnvironmentLogSource) => void;
  onPersisted?: (environmentLogId: string, content: string) => void;
}

export interface EnvironmentLogPollResult {
  status: "completed" | "canceled";
  environmentLogId: string;
  offset: bigint;
  content: string;
}

interface EnvironmentLogPollingDependencies {
  random?: () => number;
  waitForRetry?: (delayMs: number, signal: AbortSignal) => Promise<void>;
  waitAfterCompletion?: (delayMs: number, signal: AbortSignal) => Promise<void>;
}

export const ENVIRONMENT_LOG_LONG_POLL_TIMEOUT_MS = 40_000;
export const ENVIRONMENT_LOG_INITIAL_POLL_TIMEOUT_MS = 1_000;
export const ENVIRONMENT_LOG_COMPLETED_POLL_DELAY_MS = 1_000;
const ENVIRONMENT_LOG_RETRY_BASE_MS = 500;
const ENVIRONMENT_LOG_RETRY_CAP_MS = 8_000;
const ENVIRONMENT_LOG_HISTORY_PAGE_SIZE = 25;

const environmentLogClient = createConnectClient(EnvironmentService);

export async function listEnvironmentLogHistory(
  environmentId: string,
  pageToken = "",
  client: EnvironmentLogHistoryClient = environmentLogClient,
): Promise<EnvironmentLogHistoryPage> {
  const response = await client.listEnvironmentLogs({
    environmentId,
    pageParams: {
      pageSize: ENVIRONMENT_LOG_HISTORY_PAGE_SIZE,
      pageToken,
    },
  });

  return {
    items: response.items,
    nextPageToken:
      response.items.length < ENVIRONMENT_LOG_HISTORY_PAGE_SIZE
        ? ""
        : response.nextPageToken,
  };
}

export async function fetchHistoricalEnvironmentLogs(
  environmentId: string,
  environmentLogId: string,
  client: EnvironmentLogHistoryClient = environmentLogClient,
): Promise<HistoricalEnvironmentLogs> {
  const response = await client.getEnvironmentLog({
    environmentId,
    environmentLogId,
  });

  return {
    content: new TextDecoder().decode(response.content),
    etag: response.etag,
    sizeBytes: response.sizeBytes,
  };
}

export async function fetchLatestPersistedEnvironmentLogsByType(
  options: {
    environmentId: string;
    environmentLogType: EnvironmentLogType;
    signal?: AbortSignal;
  },
  client: EnvironmentLogClient = environmentLogClient,
): Promise<PersistedEnvironmentLogs> {
  const request = {
    environmentId: options.environmentId,
    environmentLogType: options.environmentLogType,
    pageParams: {
      pageSize: 1,
      pageToken: "",
    },
  };
  const page = options.signal
    ? await client.listEnvironmentLogs(request, {
        signal: options.signal,
      })
    : await client.listEnvironmentLogs(request);
  const latest = page.items[0];
  if (!latest?.logsAvailable) {
    throw new ConnectError("environment logs not found", Code.NotFound);
  }

  const logRequest = {
    environmentId: options.environmentId,
    environmentLogId: latest.environmentLogId,
  };
  const response = options.signal
    ? await client.getEnvironmentLog(logRequest, {
        signal: options.signal,
      })
    : await client.getEnvironmentLog(logRequest);

  return {
    content: response.content,
    etag: response.etag,
    sizeBytes: response.sizeBytes,
    environmentLogId: latest.environmentLogId,
  };
}

export async function fetchLatestPersistedEnvironmentBuildLogs(
  options: {
    currentEnvironmentLogId?: string;
    environmentId: string;
    signal?: AbortSignal;
  },
  client: EnvironmentLogClient = environmentLogClient,
): Promise<PersistedEnvironmentLogs | null> {
  let pageToken = "";
  let latest: EnvironmentLogSummary | undefined;

  while (!options.signal?.aborted) {
    const request = {
      environmentId: options.environmentId,
      pageParams: {
        pageSize: ENVIRONMENT_LOG_HISTORY_PAGE_SIZE,
        pageToken,
      },
    };
    const page = options.signal
      ? await client.listEnvironmentLogs(request, {
          signal: options.signal,
        })
      : await client.listEnvironmentLogs(request);
    latest = page.items.find(
      (item) =>
        ENVIRONMENT_BUILD_LOG_TYPES.has(item.environmentLogType) &&
        item.logsAvailable,
    );
    if (latest) break;
    if (!page.nextPageToken || page.nextPageToken === pageToken) break;
    pageToken = page.nextPageToken;
  }

  if (options.signal?.aborted) {
    throw new ConnectError("environment log request canceled", Code.Canceled);
  }
  if (!latest) {
    throw new ConnectError("environment build logs not found", Code.NotFound);
  }
  if (latest.environmentLogId === options.currentEnvironmentLogId) {
    return null;
  }

  const logRequest = {
    environmentId: options.environmentId,
    environmentLogId: latest.environmentLogId,
  };
  const response = options.signal
    ? await client.getEnvironmentLog(logRequest, {
        signal: options.signal,
      })
    : await client.getEnvironmentLog(logRequest);

  return {
    content: response.content,
    etag: response.etag,
    sizeBytes: response.sizeBytes,
    environmentLogId: latest.environmentLogId,
  };
}

export function isEnvironmentLogCancellation(
  error: unknown,
  signal?: AbortSignal,
): boolean {
  if (signal?.aborted) return true;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return ConnectError.from(error).code === Code.Canceled;
}

function isRetryableEnvironmentLogError(error: unknown): boolean {
  const code = ConnectError.from(error).code;
  return (
    code === Code.Unknown ||
    code === Code.DeadlineExceeded ||
    code === Code.ResourceExhausted ||
    code === Code.Internal ||
    code === Code.Unavailable
  );
}

function getRetryDelayMs(attempt: number, random: () => number): number {
  const maximumDelay = Math.min(
    ENVIRONMENT_LOG_RETRY_CAP_MS,
    ENVIRONMENT_LOG_RETRY_BASE_MS * 2 ** Math.min(attempt, 10),
  );
  return Math.floor(maximumDelay / 2 + random() * (maximumDelay / 2));
}

function waitForRetry(delayMs: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();

  return new Promise((resolve) => {
    let timeout: ReturnType<typeof setTimeout>;

    function finish() {
      clearTimeout(timeout);
      signal.removeEventListener("abort", finish);
      resolve();
    }

    timeout = setTimeout(finish, delayMs);
    signal.addEventListener("abort", finish, { once: true });
  });
}

export async function fetchPersistedEnvironmentLogsById(
  options: {
    environmentId: string;
    environmentLogId: string;
    signal?: AbortSignal;
    retryDuringHandoff?: boolean;
  },
  client: EnvironmentLogClient = environmentLogClient,
  dependencies: Pick<
    EnvironmentLogPollingDependencies,
    "random" | "waitForRetry"
  > = {},
): Promise<PersistedEnvironmentLogs> {
  const random = dependencies.random ?? Math.random;
  const wait = dependencies.waitForRetry ?? waitForRetry;
  let retryAttempt = 0;

  while (!options.signal?.aborted) {
    try {
      const request = {
        environmentId: options.environmentId,
        environmentLogId: options.environmentLogId,
      };
      const response = options.signal
        ? await client.getEnvironmentLog(request, {
            signal: options.signal,
          })
        : await client.getEnvironmentLog(request);

      return {
        content: response.content,
        etag: response.etag,
        sizeBytes: response.sizeBytes,
        environmentLogId: options.environmentLogId,
      };
    } catch (error) {
      if (isEnvironmentLogCancellation(error, options.signal)) {
        throw error;
      }

      const errorCode = ConnectError.from(error).code;
      const shouldRetryHandoff =
        options.retryDuringHandoff &&
        (errorCode === Code.NotFound || isRetryableEnvironmentLogError(error));
      if (!shouldRetryHandoff || !options.signal) {
        throw error;
      }

      const retryDelayMs = getRetryDelayMs(retryAttempt, random);
      await wait(retryDelayMs, options.signal);
      retryAttempt += 1;
    }
  }

  throw new ConnectError("environment log request canceled", Code.Canceled);
}

function canceledPollResult(
  environmentLogId: string,
  offset: bigint,
  content: string,
): EnvironmentLogPollResult {
  return {
    status: "canceled",
    environmentLogId,
    offset,
    content,
  };
}

export async function pollEnvironmentLogs(
  options: PollEnvironmentLogsOptions,
  client: EnvironmentLogClient = environmentLogClient,
  dependencies: EnvironmentLogPollingDependencies = {},
): Promise<EnvironmentLogPollResult> {
  const random = dependencies.random ?? Math.random;
  const wait = dependencies.waitForRetry ?? waitForRetry;
  const waitAfterCompletion = dependencies.waitAfterCompletion ?? waitForRetry;
  let currentEnvironmentLogId = "";
  let currentOffset = BigInt(0);
  let currentContent = "";
  let decoder = new TextDecoder();
  let retryAttempt = 0;
  let initialLongPollPending = true;
  let initialPersistedFallbackPending = true;
  const persistedEnvironmentLogIds = new Set<string>();

  const tryLoadInitialPersistedFallback = async (): Promise<boolean> => {
    try {
      const persisted = await fetchLatestPersistedEnvironmentBuildLogs(
        {
          environmentId: options.environmentId,
          signal: options.signal,
        },
        client,
      );
      if (persisted && !options.signal.aborted) {
        const persistedContent = new TextDecoder().decode(persisted.content);
        initialPersistedFallbackPending = false;
        options.onLogs(persistedContent, "persisted");
      }
    } catch (error) {
      if (isEnvironmentLogCancellation(error, options.signal)) return true;

      const code = ConnectError.from(error).code;
      if (code !== Code.NotFound && !isRetryableEnvironmentLogError(error)) {
        initialPersistedFallbackPending = false;
      }
    }
    return options.signal.aborted;
  };

  while (!options.signal.aborted) {
    let response;
    const requestedOffset = currentOffset;
    const isInitialLongPoll = initialLongPollPending;
    const timeoutMs = isInitialLongPoll
      ? ENVIRONMENT_LOG_INITIAL_POLL_TIMEOUT_MS
      : ENVIRONMENT_LOG_LONG_POLL_TIMEOUT_MS;
    initialLongPollPending = false;
    try {
      response = await client.getEnvironmentBuildLogStream(
        {
          environmentId: options.environmentId,
          offset: currentOffset,
        },
        {
          signal: options.signal,
          timeoutMs,
        },
      );
      retryAttempt = 0;
    } catch (error) {
      if (isEnvironmentLogCancellation(error, options.signal)) {
        return canceledPollResult(
          currentEnvironmentLogId,
          currentOffset,
          currentContent,
        );
      }
      if (
        isInitialLongPoll &&
        requestedOffset === BigInt(0) &&
        ConnectError.from(error).code === Code.DeadlineExceeded
      ) {
        if (
          initialPersistedFallbackPending &&
          (await tryLoadInitialPersistedFallback())
        ) {
          return canceledPollResult(
            currentEnvironmentLogId,
            currentOffset,
            currentContent,
          );
        }
        continue;
      }
      if (!isRetryableEnvironmentLogError(error)) throw error;

      await wait(getRetryDelayMs(retryAttempt, random), options.signal);
      retryAttempt += 1;
      continue;
    }

    if (options.signal.aborted) {
      return canceledPollResult(
        currentEnvironmentLogId,
        currentOffset,
        currentContent,
      );
    }

    if (
      response.content.length > 0 ||
      (response.sourceDone && response.environmentLogId)
    ) {
      initialPersistedFallbackPending = false;
    }

    if (
      initialPersistedFallbackPending &&
      response.content.length === 0 &&
      response.nextOffset === BigInt(0) &&
      !response.sourceDone
    ) {
      if (await tryLoadInitialPersistedFallback()) {
        return canceledPollResult(
          currentEnvironmentLogId,
          currentOffset,
          currentContent,
        );
      }
    }

    if (!response.environmentLogId) {
      continue;
    }

    if (
      currentEnvironmentLogId &&
      response.environmentLogId !== currentEnvironmentLogId
    ) {
      currentEnvironmentLogId = response.environmentLogId;
      currentOffset = BigInt(0);
      currentContent = "";
      decoder = new TextDecoder();
      options.onLogs("", "live");
      continue;
    }

    if (!currentEnvironmentLogId) {
      currentEnvironmentLogId = response.environmentLogId;
    }

    if (response.content.length > 0) {
      currentContent += decoder.decode(response.content, {
        stream: !response.sourceDone,
      });
      currentOffset = response.nextOffset;
      options.onLogs(currentContent, "live");
    } else if (response.sourceDone) {
      currentContent += decoder.decode();
      currentOffset = response.nextOffset;
    }

    if (!response.sourceDone) {
      continue;
    }

    if (!persistedEnvironmentLogIds.has(currentEnvironmentLogId)) {
      try {
        const persisted = await fetchPersistedEnvironmentLogsById(
          {
            environmentId: options.environmentId,
            environmentLogId: currentEnvironmentLogId,
            signal: options.signal,
            retryDuringHandoff: true,
          },
          client,
          { random, waitForRetry: wait },
        );
        if (options.signal.aborted) {
          return canceledPollResult(
            currentEnvironmentLogId,
            currentOffset,
            currentContent,
          );
        }

        currentContent = new TextDecoder().decode(persisted.content);
        persistedEnvironmentLogIds.add(currentEnvironmentLogId);
        options.onLogs(currentContent, "persisted");
        options.onPersisted?.(currentEnvironmentLogId, currentContent);
      } catch (error) {
        if (!isEnvironmentLogCancellation(error, options.signal)) throw error;
        return canceledPollResult(
          currentEnvironmentLogId,
          currentOffset,
          currentContent,
        );
      }
    }

    await waitAfterCompletion(
      ENVIRONMENT_LOG_COMPLETED_POLL_DELAY_MS,
      options.signal,
    );
  }

  return canceledPollResult(
    currentEnvironmentLogId,
    currentOffset,
    currentContent,
  );
}
